import {AppButton, AppSecondaryButton} from "@app-core/AppButton"
import BathroomImage from "assets/images/bathroom.svg"
import DressingImage from "assets/images/dressing.svg"
import EatingPreparingFoodImage from "assets/images/eatingPreparingFood.svg"
import HearingImage from "assets/images/hearing.svg"
import LifeSustainingTherapyImage from "assets/images/lifeSustainingTherapy.svg"
import MentalFunctionsImage from "assets/images/mentalFunctions.svg"
import CircleIcon from "@mui/icons-material/Circle"
import ModerateImpairmentImage from "assets/images/moderateImpairment.svg"
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material"
import SpeakingImage from "assets/images/speaking.svg"
import VisionImage from "assets/images/vision.svg"
import React, {useState, useEffect} from "react"
import WalkingImage from "assets/images/walking.svg"
import {AnswerCard} from "components/AnswerCard"
import {SELECT_ANSWER_ERROR} from "data/constants/formMessages"
import {IEligibilityQuestion} from "models/IEligibilityQuestion"
import {NAVY} from "theme"

interface EligibilityQuestionProps {
  question: IEligibilityQuestion
  questionNum: number
  totalQuestions: number
  answer: string
  onSelectAnswer: (answer: string) => void
  onNext: () => void
  onBack: () => void
}

export const EligibilityQuestion: React.FC<EligibilityQuestionProps> = (
  props: EligibilityQuestionProps
) => {
  const {
    question,
    questionNum,
    totalQuestions,
    answer,
    onSelectAnswer,
    onNext,
    onBack
  } = props
  const [selectedAnswer, setSelectedAnswer] = useState<string>("")
  const [error, setError] = useState<string>("")

  useEffect(() => {
    setSelectedAnswer(answer)
  }, [answer])

  // G2: the Next button is always active. If no answer is selected, show an
  // inline error instead of disabling/greying the button.
  const onNextClick = () => {
    if (selectedAnswer === "") {
      setError(SELECT_ANSWER_ERROR)
      return
    }
    setError("")
    onNext()
  }

  const onYesClick = () => {
    setError("")
    onSelectAnswer("Yes")
  }

  const onNoClick = () => {
    setError("")
    onSelectAnswer("No")
  }

  const setImageUri = () => {
    switch (question.type) {
      case "Hearing":
        return HearingImage
      case "Speaking":
        return SpeakingImage
      case "Vision":
        return VisionImage
      case "Walking":
        return WalkingImage
      case "Eliminating":
        return BathroomImage
      case "EatingPreparingFood":
        return EatingPreparingFoodImage
      case "Dressing":
        return DressingImage
      case "MentalFunctions":
        return MentalFunctionsImage
      case "LifeSustainingTherapy":
        return LifeSustainingTherapyImage
      case "ModerateImpairment":
      case "CumulativeEffect":
        return ModerateImpairmentImage
      default:
        return ""
    }
  }

  return (
    <Box padding={4}>
      <Box display={"flex"} alignItems="center" justifyContent="flex-start">
        <Box
          sx={{
            backgroundColor: "#ebebeb",
            borderRadius: "22px",
            minHeight: "40px",
            paddingX: "16px",
            fontSize: "16px",
            fontWeight: 500,
            color: NAVY,
            alignItems: "center",
            display: "inline-flex",
            justifyContent: "center"
          }}
        >
          {`Question ${questionNum} of ${totalQuestions}`}
        </Box>
      </Box>
      <Box
        display={"flex"}
        alignItems="center"
        justifyContent={"center"}
        flexDirection="column"
      >
        {setImageUri() !== "" && (
          <Box>
            <img src={setImageUri()} alt={question.title} />
          </Box>
        )}
        <Box>
          <Typography variant="h6" gutterBottom component="div">
            {question.title}
          </Typography>
        </Box>
      </Box>
      <Box padding={3}>
        <Box sx={{minHeight: "230px"}}>
          <Typography
            className="text-lg"
            variant="h6"
            gutterBottom
            component="div"
          >
            {question.question}
          </Typography>
          {question.options && (
            <List dense={true}>
              {question.options.map((val) => (
                <ListItem key={val}>
                  <ListItemIcon>
                    <CircleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        className="text-lg"
                        variant="h6"
                        gutterBottom
                        component="div"
                      >
                        {val}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          {question.helperNote && (
            <Typography
              component="p"
              fontSize="16px"
              fontStyle="italic"
              color={NAVY}
              lineHeight={1.6}
              marginTop={2}
              sx={{whiteSpace: "pre-line"}}
            >
              {question.helperNote}
            </Typography>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <AnswerCard
              label="Yes"
              selected={selectedAnswer === "Yes"}
              onClick={onYesClick}
              fontSize="24px"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <AnswerCard
              label="No"
              selected={selectedAnswer === "No"}
              onClick={onNoClick}
              fontSize="24px"
            />
          </Grid>
        </Grid>
      </Box>
      {error !== "" && (
        <Box display="flex" justifyContent="flex-end" marginTop={2}>
          <Typography
            fontWeight="bold"
            fontSize="18px"
            component="div"
            role="alert"
          >
            {error}
          </Typography>
        </Box>
      )}
      <Box
        display={"flex"}
        alignItems="center"
        justifyContent="flex-end"
        gap={2}
        marginTop={2}
      >
        <AppSecondaryButton onClick={onBack} sx={{minWidth: "120px"}}>
          Back
        </AppSecondaryButton>
        <AppButton onClick={onNextClick} sx={{minWidth: "150px"}}>
          Next
        </AppButton>
      </Box>
    </Box>
  )
}

export default EligibilityQuestion
